import { Link } from 'react-router-dom';
import { Store } from 'lucide-react';
import { useFeatures } from '@/hooks/useFeatures';
import { useDarkMode } from '@/hooks/useDarkMode';
import Footer from '@/components/Footer';

/**
 * Gates the public /brands pages behind the brand_directory feature flag.
 * Admins always bypass (with a heads-up banner on top).
 */
export default function BrandDirectoryGate({ user, children }) {
  const { features, loaded } = useFeatures();
  const darkMode = useDarkMode();
  const isDark = darkMode.isDark;
  const isAdmin = !!user?.is_admin;

  if (!loaded) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: darkMode.bgPrimary }}>
        <div className="animate-spin rounded-full h-10 w-10 border-b-4" style={{ borderColor: '#534145' }} />
      </div>
    );
  }

  if (!features.brand_directory && !isAdmin) {
    return (
      <div className="min-h-screen flex flex-col" style={{ background: darkMode.bgPrimary }}>
        <div className="flex-1 flex items-center justify-center px-4 py-20">
          <div className="text-center max-w-md" data-testid="brand-directory-coming-soon">
            <Store size={48} className="mx-auto mb-5" style={{ color: isDark ? '#D4A4A8' : '#534145', opacity: 0.6 }} />
            <h1
              className="mb-3"
              style={{
                fontFamily: "'Bodoni Moda', serif",
                fontWeight: 700,
                fontSize: '26px',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: isDark ? '#FFFAF5' : '#20212B',
              }}
            >
              Launching soon
            </h1>
            <p className="text-sm mb-6" style={{ fontFamily: 'Playfair Display, serif', color: isDark ? 'rgba(255,250,245,0.75)' : 'rgba(83,65,69,0.85)' }}>
              The Mementa Support-a-Brand directory — a home for small businesses worth knowing — is opening its doors very soon.
            </p>
            <Link
              to="/browse"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm"
              style={{ background: isDark ? '#D4A4A8' : '#534145', color: isDark ? '#20212B' : '#FFFAF5' }}
              data-testid="brand-gate-browse-link"
            >
              Browse gifts instead
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <>
      {!features.brand_directory && isAdmin && (
        <div
          className="text-center text-xs font-semibold py-2 px-4"
          style={{ background: '#FEF3C7', color: '#92400E' }}
          data-testid="brand-gate-admin-banner"
        >
          Hidden from the public — the Brand Directory toggle is OFF (Admin → Settings → Feature Toggles). Only admins can see this page.
        </div>
      )}
      {children}
    </>
  );
}
